import { type FC, type ReactNode, Suspense, lazy } from 'react';
import { graphql, useFragment } from 'react-relay';
import PdpSharedShippingInfoLoading from './PdpSharedShippingInfoLoading';
import { PdpSharedShippingAndReturns } from '../shared/PdpSharedShippingAndReturns/PdpSharedShippingAndReturns';

import { usePdpShippingInfoQuery } from './hooks/usePdpShippingInfoQuery';

import { type PdpSharedShippingInfoWrapper_item$key } from './__generated__/PdpSharedShippingInfoWrapper_item.graphql';
import { type PdpSharedShippingInfoWrapper_sku$key } from './__generated__/PdpSharedShippingInfoWrapper_sku.graphql';
import { type PdpSharedShippingInfoWrapper_user$key } from './__generated__/PdpSharedShippingInfoWrapper_user.graphql';

const PdpSharedShippingInfoSidebarComponent = lazy(
    () =>
        import(
            /* webpackChunkName: "PdpSharedShippingInfoSidebarComponent" */ './PdpSharedShippingInfoSidebarComponent'
        )
);

const PdpSharedShippingInfoComponent = lazy(
    () =>
        import(
            /* webpackChunkName: "PdpSharedShippingInfoComponent" */ './PdpSharedShippingInfoComponent'
        )
);

type Props = {
    item: PdpSharedShippingInfoWrapper_item$key;
    user: PdpSharedShippingInfoWrapper_user$key | null | undefined;
    currency: string;
    children?: ReactNode;
    leadTime?: string | null;
    renderTooltip?: boolean;
    isClient?: boolean;
    title?: ReactNode;
    displayInline?: boolean;
    preLinkQuoteDetails?: ReactNode;
    postLinkQuoteDetails?: ReactNode;
    postLinkNoQuoteDetails?: ReactNode;
    itemQuantity?: number | null;
    calculateLowerShippingInfo?: boolean;
    positioning?: 'sidebar' | 'seller_details';
    shortLinkCta?: boolean;
    shortRequestQuote?: boolean;
    sku?: PdpSharedShippingInfoWrapper_sku$key | null | undefined;
};

const PdpSharedShippingInfoWrapper: FC<Props> = ({
    item: itemRef,
    user: userRef,
    currency,
    children,
    leadTime,
    renderTooltip,
    isClient = false,
    title,
    displayInline,
    preLinkQuoteDetails,
    postLinkQuoteDetails,
    postLinkNoQuoteDetails,
    itemQuantity,
    calculateLowerShippingInfo = false,
    positioning,
    shortLinkCta,
    shortRequestQuote,
    sku: skuRef,
}) => {
    const user = useFragment(
        graphql`
            fragment PdpSharedShippingInfoWrapper_user on User {
                serviceId
                isVerifiedTrade
            }
        `,
        userRef
    );
    const item = useFragment(
        graphql`
            fragment PdpSharedShippingInfoWrapper_item on Item {
                serviceId
                defaultSkuId
                ...PdpSharedShippingAndReturns_item
            }
        `,
        itemRef
    );
    const sku = useFragment(
        graphql`
            fragment PdpSharedShippingInfoWrapper_sku on ItemSkuType {
                serviceId
            }
        `,
        skuRef
    );

    const itemId = item?.serviceId;
    const skuId = sku?.serviceId || item?.defaultSkuId || '';
    const userId = user?.serviceId;
    const isTradeUser = user?.isVerifiedTrade;

    const { queryReference, reloadQuery, showSpinner } = usePdpShippingInfoQuery({
        itemId,
        userId,
        isTradeUser,
        isClient,
        itemQuantity,
        skuId,
    });

    const loading =
        positioning === 'sidebar' ? (
            <PdpSharedShippingAndReturns item={item}>
                <PdpSharedShippingInfoLoading title="" />
            </PdpSharedShippingAndReturns>
        ) : (
            <PdpSharedShippingInfoLoading title={title}>{children}</PdpSharedShippingInfoLoading>
        );

    if (!queryReference || showSpinner) {
        return loading;
    }

    return (
        <Suspense fallback={loading}>
            {positioning === 'sidebar' ? (
                <PdpSharedShippingInfoSidebarComponent
                    queryReference={queryReference}
                    reloadQuery={reloadQuery}
                    currency={currency}
                    itemQuantity={itemQuantity}
                    shortLinkCta={shortLinkCta}
                    shortRequestQuote={shortRequestQuote}
                    skuId={skuId}
                />
            ) : (
                <PdpSharedShippingInfoComponent
                    queryReference={queryReference}
                    reloadQuery={reloadQuery}
                    renderTooltip={renderTooltip}
                    currency={currency}
                    leadTime={leadTime || ''}
                    userId={user?.serviceId || ''}
                    title={title}
                    displayInline={displayInline}
                    preLinkQuoteDetails={preLinkQuoteDetails}
                    postLinkQuoteDetails={postLinkQuoteDetails}
                    postLinkNoQuoteDetails={postLinkNoQuoteDetails}
                    itemQuantity={itemQuantity}
                    calculateLowerShippingInfo={calculateLowerShippingInfo}
                    skuId={skuId}
                >
                    {children}
                </PdpSharedShippingInfoComponent>
            )}
        </Suspense>
    );
};

export default PdpSharedShippingInfoWrapper;
